// src/pages/FundPlanner/components/Portfolios/Main/HoldingsReport.jsx
import React, { useMemo } from "react";
import ExcelGrid from "components/ExcelGrid";

/* ────────────────────────────────
   Local Font Overrides
──────────────────────────────── */
const SUMMARY_TEXT_CLASS = "text-app text-[12px]";
const SUMMARY_MONO_CLASS = "font-mono text-app text-[13px]";

/* ────────────────────────────────
   Helpers
──────────────────────────────── */
function trimToEmpty(value) {
  return String(value ?? "").trim();
}

function formatCurrency(value) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatPercent(value, digits = 2) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${n.toFixed(digits)}%`;
}

function formatPar(value) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function compareRows(a, b) {
  const mvA = Number(a?.market_value ?? 0);
  const mvB = Number(b?.market_value ?? 0);
  if (mvB !== mvA) return mvB - mvA;

  const issuerA = trimToEmpty(a?.issuer_name).toLowerCase();
  const issuerB = trimToEmpty(b?.issuer_name).toLowerCase();
  if (issuerA < issuerB) return -1;
  if (issuerA > issuerB) return 1;

  const idA = trimToEmpty(a?.cusip_or_id).toLowerCase();
  const idB = trimToEmpty(b?.cusip_or_id).toLowerCase();
  if (idA < idB) return -1;
  if (idA > idB) return 1;

  return 0;
}

function buildColumns() {
  return [
    {
      key: "investment_type",
      label: "Type",
      width: "min-w-[135px]",
      className: SUMMARY_TEXT_CLASS,
    },
    {
      key: "cusip_or_id",
      label: "CUSIP / ID",
      width: "min-w-[110px]",
      className: SUMMARY_MONO_CLASS,
      mono: true,
    },
    {
      key: "issuer_name",
      label: "Issuer",
      width: "min-w-[180px]",
      className: SUMMARY_TEXT_CLASS,
    },
    {
      key: "market_value",
      label: "Market Value",
      width: "min-w-[130px]",
      className: `${SUMMARY_MONO_CLASS} text-right`,
      headerClassName: "text-right",
      mono: true,
      numeric: true,
      render: (row) => formatCurrency(row.market_value),
      copyValue: (row) => formatCurrency(row.market_value),
    },
    {
      key: "percentOfTotal",
      label: "% Total",
      width: "min-w-[95px]",
      className: `${SUMMARY_MONO_CLASS} text-right`,
      headerClassName: "text-right",
      mono: true,
      numeric: true,
      render: (row) => formatPercent(row.percentOfTotal * 100),
      copyValue: (row) => formatPercent(row.percentOfTotal * 100),
    },
    {
      key: "par_amount",
      label: "Par / Face Amt",
      width: "min-w-[125px]",
      className: `${SUMMARY_MONO_CLASS} text-right`,
      headerClassName: "text-right",
      mono: true,
      numeric: true,
      render: (row) => formatPar(row.par_amount),
      copyValue: (row) => formatPar(row.par_amount),
    },
    {
      key: "effective_maturity_date",
      label: "Eff. Maturity",
      width: "min-w-[105px]",
      className: `${SUMMARY_MONO_CLASS} text-right`,
      headerClassName: "text-right",
      mono: true,
      date: true,
    },
    {
      key: "rating_rollup",
      label: "Rating",
      width: "min-w-[80px]",
      className: SUMMARY_TEXT_CLASS,
    },
  ];
}

/* ────────────────────────────────
   Component
──────────────────────────────── */
export default function HoldingsReport({ rows = [] }) {
  const gridRows = useMemo(() => {
    return [...(Array.isArray(rows) ? rows : [])]
      .sort(compareRows)
      .map((row, index) => ({
        id: row?.cusip_or_id || `holding_${index}`,
        cusip_or_id: trimToEmpty(row?.cusip_or_id) || "—",
        issuer_name: trimToEmpty(row?.issuer_name) || "—",
        investment_type: trimToEmpty(row?.investment_type) || "—",
        market_value: row?.market_value ?? null,
        percentOfTotal: row?.percentOfTotal ?? null,
        par_amount: row?.par_amount ?? null,
        effective_maturity_date: trimToEmpty(row?.effective_maturity_date) || "",
        rating_rollup: trimToEmpty(row?.rating_rollup) || "—",
      }));
  }, [rows]);

  const columns = useMemo(() => buildColumns(), []);

  if (!gridRows.length) {
    return (
      <div className="text-sm text-muted">
        No holdings are available for the selected scope and as of date.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="text-sm text-muted">
        Top 10 holdings ranked by market value.
      </div>

      <ExcelGrid columns={columns} rows={gridRows} onChange={() => {}} />
    </div>
  );
}
